import { useEffect } from "react";
import HomePage from "@/pages/home-page";
import PartnerPage from "@/pages/partner-page";
import HomeOficialPage from "@/pages/home-oficial-page";

/**
 * Formato aceito de slug de mesa — mesmo padrão de `Dashboard/shared/tenant-slugs.ts`
 * (`TENANT_SLUG_PATTERN`). Duplicado aqui de propósito: este site é um deploy separado do
 * Dashboard (Static Web App próprio, sem acesso a `@shared/*`) — se um mudar, mudar o outro junto.
 */
const TENANT_SLUG_PATTERN = /^[a-z0-9-]{2,64}$/;

/**
 * App real onde o roteamento de mesa por caminho (`htstrader.com/<mesa>`) de fato existe
 * (`resolveEffectiveTenant` + `tenant-slug.ts`, do lado do Dashboard). Este site (hts-site) só
 * conhece 2 rotas — "/" e "/founding" — e nunca vai ganhar mais: qualquer outro 1º segmento é,
 * por eliminação, link público de mesa branca, não rota daqui.
 */
const APP_HOST = "https://app.htstrader.com.br";

/**
 * Caminhos da vitrine que sempre vão para o app: `/v/<slug do produto>` (mesmo formato de `SLUG_DO_PRODUTO` em
 * `Dashboard/shared/vitrine/produto.ts`), `/loja`, `/loja/<slug>` e `/meus-produtos`. Duplicado pelo mesmo motivo do
 * padrão acima (deploy separado, sem `@shared/*`).
 */
const VITRINE_NO_APP = /^\/(v\/[a-z0-9][a-z0-9-]{2,59}|loja(\/[a-z0-9][a-z0-9-]{2,59})?|meus-produtos)$/;

// Roteamento simples por pathname, sem lib de router num site estático leve. Desde 2026-09-19
// "/" e "/founding" mostram a MESMA página de venda (ver home-page.tsx): com a pré-venda aberta,
// quem chegava pelo orgânico caía na lista de espera e não via onde comprar.
//
// Achado em produção (2026-09-06, dono testando link de mesa branca): `www.htstrader.com/<mesa>`
// (ex.: `/dailydolar`) caía no fallback de SPA deste site e mostrava a WAITLIST — os dois domínios
// "www.htstrader.com(.br)" foram bindados neste Static Web App (marketing), enquanto o
// roteamento por tenant mora só no app real. Sem isso, todo link público de mesa branca quebra.
// Fix: qualquer 1º segmento que não seja rota conhecida daqui e pareça slug válido redireciona
// (client-side — Static Web App não tem como fazer isso via config declarativo com o caminho
// preservado dinamicamente) pro app real, path e query string intactos.
//
// VITE_PREVIEW_PAGE força uma página específica no build, ignorando o pathname — só usado pra
// gerar os artifacts de preview interno (cada página vira um link separado, já que o artifact não
// tem servidor de rotas de verdade). Nunca setado no build real de produção.
export default function App() {
  const forced = import.meta.env.VITE_PREVIEW_PAGE;

  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  const firstSegment = path === "/" ? "" : (path.split("/").filter(Boolean)[0] || "").toLowerCase();
  // `/p/<slug>` = link de parceiro B2B (2026-09-17). Checado ANTES do redirect de mesa: "p" casa
  // com o padrão de slug de mesa, e sem isto todo link de parceiro iria parar no app.
  const partnerMatch = path.match(/^\/p\/([a-z0-9][a-z0-9-]{1,39})$/i);
  // `/recursos` (30/09) são páginas HTML prontas, geradas no build (scripts/gerar-recursos.mjs): nunca vão para o app.
  const looksLikeTenantSlug = !partnerMatch && path !== "/" && path !== "/founding" && firstSegment !== "recursos" && TENANT_SLUG_PATTERN.test(firstSegment);
  // Vitrine (20/10/2026): a página do produto (`/v/<slug>`) e a loja moram no app. `/v` tem 1 letra e não casa com o
  // padrão de slug de mesa: sem esta linha, o link do produto aberto em htstrader.com.br caía na home do site.
  const ehDaVitrine = VITRINE_NO_APP.test(path);
  const vaiParaOApp = looksLikeTenantSlug || ehDaVitrine;

  useEffect(() => {
    if (forced || !vaiParaOApp) return;
    window.location.replace(`${APP_HOST}${window.location.pathname}${window.location.search}`);
    // Sem cleanup: a navegação já saiu desta página.
  }, [forced, vaiParaOApp]);

  if (forced) return <HomePage />;

  // Tela em branco de propósito enquanto o `window.location.replace` acontece (é síncrono o
  // bastante pra ninguém ver isto na prática) — existe só pra não piscar a waitlist errada no
  // meio do caminho, que era o que a implementação anterior fazia.
  if (vaiParaOApp) return null;

  if (partnerMatch) return <PartnerPage slug={partnerMatch[1].toLowerCase()} />;
  // Home oficial, venda aberta (01/10), em magenta. A home da pré-venda fica só em `/founding`.
  if (path === "/") return <HomeOficialPage />;
  return <HomePage />;
}
